import { useEffect } from "react";
import { useLocation } from "@tanstack/react-router";
import { useMixpanel } from "@/hooks/useMixpanel";

/**
 * UTMTracker component that automatically tracks UTM campaigns
 * whenever a user lands on any page with ?utm=CAMPAIGN_NAME
 */
export function UTMTracker() {
  const location = useLocation();
  const { trackUTMCampaign } = useMixpanel();

  useEffect(() => {
    // Parse URL parameters
    const searchParams = new URLSearchParams(location.search);
    const utmCampaign = searchParams.get("utm");

    // If utm parameter exists, track the campaign
    if (utmCampaign) {
      // Collect additional UTM parameters if they exist
      const additionalProperties: Record<string, string> = {};

      // Standard UTM parameters
      const utmSource = searchParams.get("utm_source");
      const utmMedium = searchParams.get("utm_medium");
      const utmTerm = searchParams.get("utm_term");
      const utmContent = searchParams.get("utm_content");

      if (utmSource) additionalProperties.utm_source = utmSource;
      if (utmMedium) additionalProperties.utm_medium = utmMedium;
      if (utmTerm) additionalProperties.utm_term = utmTerm;
      if (utmContent) additionalProperties.utm_content = utmContent;

      // Add referrer information if available
      if (document.referrer) {
        additionalProperties.referrer = document.referrer;
      }

      console.log("Tracking UTM campaign", utmCampaign, additionalProperties);

      // Track the UTM campaign
      trackUTMCampaign(utmCampaign, additionalProperties);

      // Optional: Store UTM data in localStorage for session tracking
      try {
        const utmData = {
          campaign: utmCampaign,
          timestamp: new Date().toISOString(),
          page: location.pathname,
          ...additionalProperties,
        };
        localStorage.setItem("utm_campaign_data", JSON.stringify(utmData));
      } catch (error) {
        // Silently fail if localStorage is not available
        console.warn("Could not store UTM data in localStorage:", error);
      }
    }
  }, [location.search, location.pathname, trackUTMCampaign]);

  // This component doesn't render anything
  return null;
}
